import { InjectionToken, Signal, signal } from '@angular/core';

/**
 * Capability flags consumed by the document-management UI to gate tag-related actions. The lib
 * itself doesn't depend on the host app's auth / resource-access machinery — the host provides
 * an implementation via {@link DOCUMENT_MANAGEMENT_CAPABILITIES}, and the lib defaults to
 * "fully capable" so component-level usage in tests and isolated dev hosts works without setup.
 *
 * - `canUpdateDocuments`: maps to the `documents.update` resource action — required for PATCHing
 *   a document or folder, which is the path that attaches/detaches tags and the path the backend
 *   uses for the **restore** endpoint. Gates the "+ Add tag" affordance, the × on existing chips,
 *   and the Restore button in the detail pane.
 * - `canDeleteDocuments`: maps to the `documents.delete` resource action — required for both the
 *   **soft-delete** and the **hard-delete** endpoints. Gates the Delete button on
 *   non-soft-deleted rows and the Delete button on soft-deleted rows.
 * - `canCreateTags`: maps to the `tags.create` resource action (backed by the `manageTags` grant)
 *   — required for `POST /tags`. Gates the "Create '<query>'" inline-create suggestion in the
 *   tag-editor's autocomplete dropdown. Independent from `canUpdateDocuments` so we can express
 *   the "can attach existing tags but not create new ones" tier the product calls out.
 * - `canCreateDocuments`: maps to the `documents.create` resource action — required for both
 *   `POST /documents/initiate-upload` (file upload) and `POST /folders` (folder creation). Gates
 *   the upload and "New folder" affordances in the folder browser; users without the grant see
 *   the buttons disabled with a "no permission" tooltip rather than hitting a 403/422.
 */
export interface DocumentManagementCapabilities {
  readonly canUpdateDocuments: Signal<boolean>;
  readonly canDeleteDocuments: Signal<boolean>;
  readonly canCreateTags: Signal<boolean>;
  readonly canCreateDocuments: Signal<boolean>;
}

const FULL_CAPABILITIES: DocumentManagementCapabilities = {
  canUpdateDocuments: signal(true).asReadonly(),
  canDeleteDocuments: signal(true).asReadonly(),
  canCreateTags: signal(true).asReadonly(),
  canCreateDocuments: signal(true).asReadonly(),
};

export const DOCUMENT_MANAGEMENT_CAPABILITIES = new InjectionToken<DocumentManagementCapabilities>(
  'DocumentManagementCapabilities',
  {
    providedIn: 'root',
    factory: () => FULL_CAPABILITIES,
  },
);
